import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Download, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { useIsAdmin } from "@/shared/auth";
import { toast } from "@/shared/lib/toast";
import { Drawer } from "@/shared/ui/drawer";
import { Skeleton } from "@/shared/ui/skeleton";

import { useAuditLog } from "../api/use-audit-log";
import { auditLogNavigationFor } from "../lib/audit-log-link";

type AuditEvent = components["schemas"]["AuditEvent"];
type Filter = "all" | "notable";

const SEV_DOT: Record<string, string> = {
  high: "var(--intent-danger-fg)",
  notable: "var(--brand-500)",
  info: "var(--brand-400)",
};
const SEV_BADGE: Record<string, string> = {
  info: "bg-[var(--intent-neutral-bg)] text-[var(--intent-neutral-fg)]",
  notable: "bg-[var(--brand-50)] text-[var(--brand-700)]",
  high: "bg-[var(--intent-danger-bg)] text-[var(--intent-danger-fg)]",
};

const SEV_KEY: Record<string, string> = {
  info: "audit.sev_info",
  notable: "audit.sev_notable",
  high: "audit.sev_high",
};

// Fixed locale so digit order/format is deterministic, without a forced
// timeZone so this renders in the viewer's local time — same approach as
// features/audit-log/components/AuditTable.tsx's formatLocalDateTime
// (duplicated rather than imported: features may not import one another,
// see CLAUDE.md rule 2).
const localDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const localTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function formatAt(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${localDateFormatter.format(d)} ${localTimeFormatter.format(d)}`;
}

/** Only a known-safe key is surfaced from the free-form `context` object —
 * never an arbitrary dump of whatever the backend happened to record. */
function safeOrderId(context: AuditEvent["context"]): string | undefined {
  const value = context?.order_id;
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

interface AuditDrawerProps {
  open: boolean;
  onClose: () => void;
  sku: string;
  mkp: string;
  productName?: string;
}

export function AuditDrawer({
  open,
  onClose,
  sku,
  mkp,
  productName,
}: AuditDrawerProps): JSX.Element {
  const { t } = useTranslation("inventory");
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAuditLog(sku, mkp, open);

  const events = data?.pages.flatMap((p) => p.items ?? []) ?? [];

  const visible = events.filter((e: AuditEvent) =>
    filter === "all" ? true : e.severity !== "info",
  );

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const exportActions = (
    <button
      type="button"
      onClick={() => toast.success(t("audit.export_csv"))}
      className="grid h-8 w-8 place-items-center rounded-lg text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] focus-ring"
      aria-label={t("audit.export_csv")}
    >
      <Download className="h-4 w-4" />
    </button>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={<span data-testid="audit-drawer-title">{t("audit.drawer_title")}</span>}
      subtitle={productName ? `${sku} · ${productName}` : sku}
      actions={exportActions}
    >
      {/* Filter chips */}
      <div className="flex gap-2 border-b border-[var(--premium-border)] px-5 py-3">
        {(["all", "notable"] as const).map((f) => (
          <button
            key={f}
            type="button"
            data-testid={`audit-filter-${f}`}
            onClick={() => setFilter(f)}
            className="rounded-full border px-3 py-1 text-[11.5px] font-semibold transition"
            style={
              filter === f
                ? {
                    background: "var(--brand-50)",
                    borderColor: "var(--brand-500)",
                    color: "var(--brand-700)",
                  }
                : {
                    borderColor: "var(--premium-border-strong)",
                    color: "var(--text-secondary)",
                    background: "#fff",
                  }
            }
          >
            {f === "all" ? t("audit.filter_all") : t("audit.filter_notable")}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {isLoading ? (
        <div className="space-y-4 px-5 py-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
          <p className="text-sm text-[var(--text-subtle)]">{t("audit.error")}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-lg border border-[var(--premium-border)] px-4 py-1.5 text-sm font-medium transition hover:bg-[var(--canvas)]"
          >
            {t("audit.retry")}
          </button>
        </div>
      ) : visible.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-[var(--text-subtle)]">
          {t("audit.empty")}
        </p>
      ) : (
        <>
          <ol data-testid="audit-timeline" className="px-5 py-2">
            {visible.map((e, i) => {
              const sev = e.severity ?? "info";
              return (
                <li
                  key={e.id ?? i}
                  data-testid="audit-event"
                  className="relative border-l-2 py-4 pl-6"
                  style={{
                    borderColor: i === visible.length - 1 ? "transparent" : "var(--premium-border)",
                  }}
                >
                  <span
                    className="absolute -left-[7px] top-[19px] h-3 w-3 rounded-full bg-white"
                    style={{ border: `2px solid ${SEV_DOT[sev] ?? SEV_DOT.info}` }}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13.5px] font-bold">{e.action}</span>
                    {e.at ? (
                      <time
                        dateTime={e.at}
                        title={e.at}
                        className="whitespace-nowrap text-[11.5px] text-[var(--text-subtle)]"
                      >
                        {formatAt(e.at)}
                        <span className="sr-only">
                          {" "}
                          {t("audit.time_utc_suffix", { utc: e.at })}
                        </span>
                      </time>
                    ) : (
                      <span className="whitespace-nowrap text-[11.5px] text-[var(--text-subtle)]">
                        {formatAt(e.at)}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                    <span>{e.actor || t("audit.actor_system")}</span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase ${SEV_BADGE[sev] ?? SEV_BADGE.info}`}
                    >
                      {t(SEV_KEY[sev] ?? "audit.sev_info")}
                    </span>
                  </div>
                  {safeOrderId(e.context) && (
                    <div
                      data-testid="audit-event-order-context"
                      className="mt-1 text-[11.5px] text-[var(--text-subtle)]"
                    >
                      {t("audit.order_context", { id: safeOrderId(e.context) })}
                    </div>
                  )}
                  {e.field_changes && e.field_changes.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1.5">
                      {e.field_changes.map((fc, fci) => (
                        <div
                          key={fci}
                          className="num inline-flex w-fit items-center gap-2 rounded-lg border border-[var(--premium-border)] bg-[var(--canvas)] px-2.5 py-1.5 text-[12.5px]"
                        >
                          <span className="text-[11px] font-semibold text-[var(--text-subtle)]">
                            {fc.field}
                          </span>
                          <span className="text-[var(--text-subtle)] line-through">
                            {String(fc.old ?? "—")}
                          </span>
                          <ArrowRight className="h-3.5 w-3.5 text-[var(--text-subtle)]" />
                          <span className="font-semibold">{String(fc.new ?? "—")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => void navigate(auditLogNavigationFor(sku, e))}
                      className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--brand-600)] transition hover:text-[var(--brand-700)] focus-ring"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden />
                      {t("audit.view_in_log")}
                    </button>
                  )}
                </li>
              );
            })}
          </ol>
          <div ref={sentinelRef} className="h-1" />
          {isFetchingNextPage && (
            <div className="px-5 py-3 text-center text-xs text-[var(--text-subtle)]">
              Loading more…
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
